import {
  Box,
  Button,
  Checkbox,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, type FC, ChangeEvent, RefObject, useEffect } from "react";
import {
  API_ASYNC_INFER_EDIT_WITH_PROMPT,
  API_ASYNC_INFER_REPLACE_ANY,
  API_ASYNC_INFER_VIDEO,
  ERR_MSG_NOT_LOGIN,
  ERR_MSG_NO_TASK_ID,
  INFER_TASK_STATUS_DISPLAY_ENUM,
  INFER_TASK_STATUS_ENUM,
  MAGIC_SHORTCUT_PARAM_TYPE_ENUM,
  MAGIC_TYPE_DISPLAY_ENUM,
  MAGIC_TYPE_ENUM,
} from "../../utils/constants";
import {
  MagicAPIRequestBodyType,
  cancelTask,
  getTaskResultEvery3s,
} from "../../utils/http.util";
import { base64ToImgUrl } from "../../utils/image.util";
import { Txt2ImgSizeModal } from "../modals/Txt2ImgSizeModal";
import { MainMagicShortcutType } from "../main-page/Shortcuts";

type FinalMagicActionsProps = {
  magicType: MAGIC_TYPE_ENUM | null;
  showPromptMsg: (str: string) => void;
  onMagicSuccess: (url: string) => void;
  magicUrl: string;
  textPrompt: string;
  magicShortcutObj: MainMagicShortcutType | null;
  originCanvasRef: RefObject<HTMLCanvasElement | null>;
  maskCanvasRef: RefObject<HTMLCanvasElement | null>;
  referenceCanvasRef: RefObject<HTMLCanvasElement | null> | null;
  onMagicFileGeneratingChange: (generating: boolean) => void;
  onMagicGenerateClick: (url: string) => void;
  onContinueEditClick: () => void;
  onReEditClick: () => void;
};

export const FinalMagicActions: FC<FinalMagicActionsProps> = (props) => {
  // responsive UI
  const theme = useTheme();
  const notPhone = useMediaQuery(theme.breakpoints.up("sm"));

  const [taskId, setTaskId] = useState("");
  const [taskStatus, setTaskStatus] = useState<string>("");
  const [totalTasks, setTotalTasks] = useState(0);
  const [inFocusMode, setInFocusMode] = useState(false);
  const [isEnhance, setIsEnhance] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [showTxt2ImgSizePanel, setShowTxt2ImgSizePanel] = useState(false);

  useEffect(() => {
    if (
      taskStatus === INFER_TASK_STATUS_ENUM.inProgress ||
      (taskStatus === INFER_TASK_STATUS_ENUM.waiting && totalTasks < 1)
    ) {
      setGenerating(true);
      props.onMagicFileGeneratingChange(true);
    } else {
      setGenerating(false);
      props.onMagicFileGeneratingChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskStatus]);

  const handleMagicTaskIdUpdate = (tid: string) => setTaskId(tid);

  const handleMagicTaskStatusUpdate = (status: string, idx: number) => {
    console.log(
      " -> FinalMagicActions-handleMagicTaskStatusUpdate, status:",
      status,
      ", idx:",
      idx
    );
    setTaskStatus(status);
    setTotalTasks(idx);
  };

  const handleMagicGetTaskResult = (response: any) => {
    console.log(
      "-> FinalMagicActions-handleMagicGetTaskResult, response:",
      response
    );

    setGenerating(false);
    props.onMagicFileGeneratingChange(false);

    if (response === ERR_MSG_NOT_LOGIN) {
      props.showPromptMsg(ERR_MSG_NOT_LOGIN);
    } else if (typeof response === "string" && response.includes(":")) {
      const code = response.split(": ")[0];
      if (parseInt(code) === 5) {
        // "5: task a241ef337ab7987d has been canceled"
        setTaskStatus(INFER_TASK_STATUS_ENUM.down);
        setTaskId("");
        setTotalTasks(0);
      } else {
        props.showPromptMsg(response);
      }
    } else {
      if (response?.data?.length > 0 && typeof response.data[0] === "string") {
        props.onMagicSuccess(response.data[0]);
      }
    }
  };

  const finalMagicGetOriginBase64 = () => {
    const isOriginCanvasVisible =
      props.originCanvasRef?.current?.style?.visibility === "visible";

    if (isOriginCanvasVisible) {
      const base64 = props.originCanvasRef?.current?.toDataURL("image/jpeg");

      if (base64) {
        return base64;
      }
    }

    console.log(
      " -> FinalMagicActions-finalMagicGetOriginBase64, originCanvas:",
      props.originCanvasRef?.current
    );

    return "";
  };

  const finalMagicGetMaskBase64 = () => {
    const isMaskCanvasVisible =
      props.maskCanvasRef?.current?.style?.visibility === "visible";

    if (isMaskCanvasVisible) {
      const base64 = props.maskCanvasRef?.current?.toDataURL("image/jpeg");

      if (base64) {
        return base64;
      }
    }

    console.log(
      " -> FinalMagicActions-finalMagicGetMaskBase64, maskCanvas:",
      props.maskCanvasRef?.current
    );

    return "";
  };

  const finalMagicGetReferenceBase64 = () => {
    const isReferenceCanvasVisible =
      props.referenceCanvasRef?.current?.style?.visibility === "visible";

    if (isReferenceCanvasVisible) {
      const base64 = props.referenceCanvasRef?.current?.toDataURL("image/jpeg");

      if (base64) {
        return base64;
      }
    }

    console.log(
      " -> FinalMagicActions-finalMagicGetReferenceBase64, referenceCanvas:",
      props.referenceCanvasRef?.current
    );

    return "";
  };

  const handleMagicGenerateClick = async () => {
    console.log(
      " -> FinalMagicActions-handleMagicGenerateClick, magicType:",
      props.magicType
    );

    setGenerating(true);
    props.onMagicFileGeneratingChange(true);

    const originBase64 = finalMagicGetOriginBase64();
    if (originBase64) {
      const url = base64ToImgUrl(originBase64.split(",")[1]);
      props.onMagicGenerateClick(url);
    } else {
      props.onMagicGenerateClick("");
    }

    if (props.magicShortcutObj) {
      // 1. 快捷功能
      console.log(
        " -> FinalMagicActions-handleMagicGenerateClick, magicShortcutObj:",
        props.magicShortcutObj
      );

      if (props.magicType === MAGIC_TYPE_ENUM.powerful) {
        // 1.1. 快捷功能 - 万能修 - 变换天气、消除人群
        if (
          props.textPrompt &&
          props.magicShortcutObj?.params.init_image ===
            MAGIC_SHORTCUT_PARAM_TYPE_ENUM.required &&
          originBase64
        ) {
          const requestBody: MagicAPIRequestBodyType = {
            init_image: originBase64.split(",")[1],
            text_prompt: props.textPrompt,
          };

          if (inFocusMode) {
            requestBody.app_type = "focus_edit";
          }

          if (isEnhance) {
            requestBody.enhance = true;
          }

          const taskResultResp = await getTaskResultEvery3s(
            API_ASYNC_INFER_EDIT_WITH_PROMPT,
            requestBody,
            handleMagicTaskIdUpdate,
            handleMagicTaskStatusUpdate
          );

          handleMagicGetTaskResult(taskResultResp);
        } else {
          if (!originBase64) {
            props.showPromptMsg(
              `万能修-${
                props.magicShortcutObj.type || "快捷功能"
              }：未上传原图！`
            );
          } else {
            props.showPromptMsg(
              `万能修-${
                props.magicShortcutObj.type || "快捷功能"
              }：未填写提示词！`
            );
          }
        }
      } else {
        // 1.2. 快捷功能 - 精准修 - 更换背景
        const maskBase64 = finalMagicGetMaskBase64();
        const referenceBase64 = finalMagicGetReferenceBase64();

        if (originBase64 && maskBase64 && referenceBase64) {
          // 1.2.1 快捷功能 - 精准修 - 底图 + mask + 参考图：将参考图提取特征后在底图 mask涂抹区域生成
          const requestBody: MagicAPIRequestBodyType = {
            init_image: originBase64.split(",")[1],
            mask_image: maskBase64.split(",")[1],
            reference_image: referenceBase64.split(",")[1],
            text_prompt: props.textPrompt,
          };

          if (inFocusMode) {
            requestBody.app_type = "focus_edit";
          }

          const taskResultResp = await getTaskResultEvery3s(
            API_ASYNC_INFER_REPLACE_ANY,
            requestBody,
            handleMagicTaskIdUpdate,
            handleMagicTaskStatusUpdate
          );
          handleMagicGetTaskResult(taskResultResp);
        } else if (originBase64 && maskBase64 && props.textPrompt) {
          // 1.2.2. 快捷功能 - 精准修 - 底图 + mask + 提示词
          const requestBody: MagicAPIRequestBodyType = {
            init_image: originBase64.split(",")[1],
            mask_image: maskBase64.split(",")[1],
            text_prompt: props.textPrompt,
          };

          if (inFocusMode) {
            requestBody.app_type = "focus_edit";
          }

          const taskResultResp = await getTaskResultEvery3s(
            API_ASYNC_INFER_REPLACE_ANY,
            requestBody,
            handleMagicTaskIdUpdate,
            handleMagicTaskStatusUpdate
          );

          handleMagicGetTaskResult(taskResultResp);
        } else {
          if (!originBase64) {
            props.showPromptMsg(
              `精准修-${
                props.magicShortcutObj.type || "快捷功能"
              }：未上传原图！`
            );
          } else if (!maskBase64) {
            props.showPromptMsg(
              `精准修-${
                props.magicShortcutObj.type || "快捷功能"
              }：未进行重绘操作！`
            );
          } else {
            props.showPromptMsg(
              `精准修-${
                props.magicShortcutObj.type || "快捷功能"
              }：未上传参考图、未填写提示词！`
            );
          }
        }
      }
    } else {
      // 2. 非快捷功能
      if (props.magicType === MAGIC_TYPE_ENUM.partial) {
        // 2.1. 非快捷功能 - 精准修 - replace_with_any
        // 2.1.1. 非快捷功能 - 精准修 - 底图 + mask + 参考图：将参考图提取特征后在底图 mask涂抹区域生成
        const maskBase64 = finalMagicGetMaskBase64();
        const referenceBase64 = finalMagicGetReferenceBase64();

        if (originBase64 && maskBase64 && referenceBase64) {
          const requestBody: MagicAPIRequestBodyType = {
            init_image: originBase64.split(",")[1],
            mask_image: maskBase64.split(",")[1],
            reference_image: referenceBase64.split(",")[1],
            text_prompt: props.textPrompt,
          };

          if (inFocusMode) {
            requestBody.app_type = "focus_edit";
          }

          const taskResultResp = await getTaskResultEvery3s(
            API_ASYNC_INFER_REPLACE_ANY,
            requestBody,
            handleMagicTaskIdUpdate,
            handleMagicTaskStatusUpdate
          );
          handleMagicGetTaskResult(taskResultResp);
        } else if (originBase64 && maskBase64 && props.textPrompt) {
          // 2.1.2. 非快捷功能 - 精准修 - 底图 + mask + 提示词 - replace_with_any
          const requestBody: MagicAPIRequestBodyType = {
            init_image: originBase64.split(",")[1],
            mask_image: maskBase64.split(",")[1],
            text_prompt: props.textPrompt,
          };

          if (inFocusMode) {
            requestBody.app_type = "focus_edit";
          }

          const taskResultResp = await getTaskResultEvery3s(
            API_ASYNC_INFER_REPLACE_ANY,
            requestBody,
            handleMagicTaskIdUpdate,
            handleMagicTaskStatusUpdate
          );

          handleMagicGetTaskResult(taskResultResp);
        } else if (props.textPrompt) {
          // 2.1.3. 非快捷功能 - 精准修 - 文生图 - replace_with_any
          // 显示“文生图 - 设置生成图片尺寸”对话框
          setShowTxt2ImgSizePanel(true);
        } else {
          if (originBase64 && !maskBase64) {
            props.showPromptMsg(`精准修：未进行重绘操作！`);
          } else if (
            originBase64 &&
            maskBase64 &&
            (!referenceBase64 || !props.textPrompt)
          ) {
            props.showPromptMsg(`精准修：未上传参考图、未填写提示词！`);
          } else {
            props.showPromptMsg(`精准修-文生图：未填写提示词！`);
          }
        }
      } else if (props.magicType === MAGIC_TYPE_ENUM.powerful) {
        // 2.2. 非快捷功能 - 万能修
        // 2.2.1. 非快捷功能 - 万能修 - 底图 + 提示词 - edit_with_prompt
        if (originBase64 && props.textPrompt) {
          const requestBody: MagicAPIRequestBodyType = {
            init_image: originBase64.split(",")[1],
            text_prompt: props.textPrompt,
          };

          if (inFocusMode) {
            requestBody.app_type = "focus_edit";
          }

          if (isEnhance) {
            requestBody.enhance = true;
          }

          const taskResultResp = await getTaskResultEvery3s(
            API_ASYNC_INFER_EDIT_WITH_PROMPT,
            requestBody,
            handleMagicTaskIdUpdate,
            handleMagicTaskStatusUpdate
          );

          handleMagicGetTaskResult(taskResultResp);
        } else if (props.textPrompt) {
          // 2.2.2. 非快捷功能 - 万能修 - 文生图 - edit_with_prompt
          // 显示“文生图 - 设置生成图片尺寸”对话框
          setShowTxt2ImgSizePanel(true);
        } else {
          if (!originBase64) {
            props.showPromptMsg(`万能修-文生图：未填写提示词！`);
          } else {
            props.showPromptMsg(`万能修：未填写提示词！`);
          }
        }
      } else {
        // 2.3. 非快捷功能 - 图生视频 - 底图 + 提示词 - image_to_video
        if (originBase64 && props.textPrompt) {
          const requestBody: MagicAPIRequestBodyType = {
            init_image: originBase64.split(",")[1],
            text_prompt: props.textPrompt,
          };

          const taskResultResp = await getTaskResultEvery3s(
            API_ASYNC_INFER_VIDEO,
            requestBody,
            handleMagicTaskIdUpdate,
            handleMagicTaskStatusUpdate
          );

          handleMagicGetTaskResult(taskResultResp);
        } else {
          if (!originBase64) {
            props.showPromptMsg(`图生视频：未上传原图！`);
          } else if (!props.textPrompt) {
            props.showPromptMsg(`图生视频：未填写提示词！`);
          } else {
            props.showPromptMsg(`图生视频：请上传原图，填写提示词！`);
          }
        }
      }
    }

    setGenerating(false);
    props.onMagicFileGeneratingChange(false);
  };

  const handleMagicCancelTaskClick = async () => {
    if (taskId) {
      const data = await cancelTask(taskId);
      if (data === ERR_MSG_NOT_LOGIN) {
        props.showPromptMsg(ERR_MSG_NOT_LOGIN);
      } else if (typeof data === "string" && data.includes(":")) {
        props.showPromptMsg(data);
      } else {
        setGenerating(false);
        props.onMagicFileGeneratingChange(false);
        setTaskStatus(INFER_TASK_STATUS_ENUM.down);
      }
    } else {
      props.showPromptMsg(ERR_MSG_NO_TASK_ID);
    }
  };

  const handleMagicDownloadClick = () => {
    if (props.magicUrl) {
      window.open(props.magicUrl, "_blank");
    }
  };

  const handleContinueEditClick = () => {
    console.log(" -> FinalMagicActions-handleContinueEditClick");
    props.onContinueEditClick();

    setTaskId("");
    setTaskStatus("");
    setTotalTasks(0);
    setInFocusMode(false);
    setIsEnhance(false);
    setGenerating(false);
    setShowTxt2ImgSizePanel(false);
    props.onMagicFileGeneratingChange(false);
  };

  const handleReEditClick = () => {
    console.log(" -> FinalMagicActions-handleReEditClick");
    props.onReEditClick();

    setTaskId("");
    setTaskStatus("");
    setTotalTasks(0);
    setInFocusMode(false);
    setIsEnhance(false);
    setGenerating(false);
    setShowTxt2ImgSizePanel(false);
    props.onMagicFileGeneratingChange(false);
  };

  const handleTxt2ImgSizeModalClose = async (size: {
    width: number;
    height: number;
  }) => {
    setShowTxt2ImgSizePanel(false);

    // 2.2.2. 非快捷功能 - 万能修 - 文生图 - edit_with_prompt
    if (
      !props.magicShortcutObj &&
      props.magicType === MAGIC_TYPE_ENUM.powerful
    ) {
      const requestBody: MagicAPIRequestBodyType = {
        text_prompt: props.textPrompt,
      };

      if (isEnhance) {
        requestBody.enhance = true;
      }

      if (size && size.width > 0 && size.height > 0) {
        requestBody.width = size.width;
        requestBody.height = size.height;
      }

      const taskResultResp = await getTaskResultEvery3s(
        API_ASYNC_INFER_EDIT_WITH_PROMPT,
        requestBody,
        handleMagicTaskIdUpdate,
        handleMagicTaskStatusUpdate
      );

      handleMagicGetTaskResult(taskResultResp);
    }

    // 2.1.3. 非快捷功能 - 精准修 - 文生图 - replace_with_any
    if (
      !props.magicShortcutObj &&
      props.magicType === MAGIC_TYPE_ENUM.partial
    ) {
      const requestBody: MagicAPIRequestBodyType = {
        text_prompt: props.textPrompt,
      };

      if (size && size.width > 0 && size.height > 0) {
        requestBody.width = size.width;
        requestBody.height = size.height;
      }

      const taskResultResp = await getTaskResultEvery3s(
        API_ASYNC_INFER_REPLACE_ANY,
        requestBody,
        handleMagicTaskIdUpdate,
        handleMagicTaskStatusUpdate
      );

      handleMagicGetTaskResult(taskResultResp);
    }
  };

  return (
    <Grid
      size={{ xs: 12 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "0 16px",
        marginTop: "-8px",
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "8px",
          flexWrap: notPhone ? "nowrap" : "wrap",
        }}
      >
        {(props.magicType === MAGIC_TYPE_ENUM.powerful ||
          props.magicType === MAGIC_TYPE_ENUM.partial) &&
          taskStatus !== INFER_TASK_STATUS_ENUM.waiting && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Checkbox
                checked={inFocusMode}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setInFocusMode(e.target.checked)
                }
                disabled={generating || !!props.magicUrl}
                size="small"
                color={
                  generating || !!props.magicUrl
                    ? "info"
                    : props.magicShortcutObj
                    ? "secondary"
                    : "primary"
                }
              />
              <Typography
                variant="body2"
                sx={{ width: "60px", marginLeft: "-8px" }}
                textAlign="left"
                color={
                  generating || !!props.magicUrl
                    ? theme.palette.grey[600]
                    : props.magicShortcutObj
                    ? theme.palette.secondary.main
                    : theme.palette.common.black
                }
                noWrap
              >
                专注模式
              </Typography>
            </Box>
          )}

        {props.magicType === MAGIC_TYPE_ENUM.powerful &&
          taskStatus !== INFER_TASK_STATUS_ENUM.waiting && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Checkbox
                checked={isEnhance}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setIsEnhance(e.target.checked)
                }
                disabled={generating || !!props.magicUrl}
                size="small"
                color={
                  generating || !!props.magicUrl
                    ? "info"
                    : props.magicShortcutObj
                    ? "secondary"
                    : "primary"
                }
              />
              <Typography
                variant="body2"
                sx={{ width: "40px", marginLeft: "-8px" }}
                textAlign="left"
                color={
                  generating || !!props.magicUrl
                    ? theme.palette.grey[600]
                    : props.magicShortcutObj
                    ? theme.palette.secondary.main
                    : theme.palette.common.black
                }
                noWrap
              >
                精绘
              </Typography>
            </Box>
          )}

        {taskStatus !== INFER_TASK_STATUS_ENUM.waiting && (
          <Button
            variant="contained"
            onClick={handleMagicGenerateClick}
            disabled={generating || !!props.magicUrl}
            color={props.magicShortcutObj ? "secondary" : "primary"}
          >
            {generating
              ? INFER_TASK_STATUS_DISPLAY_ENUM.inProgress
              : props.magicType === MAGIC_TYPE_ENUM.partial
              ? `开始${
                  MAGIC_TYPE_DISPLAY_ENUM.partial.split("（")[0] || "修图"
                }`
              : props.magicType === MAGIC_TYPE_ENUM.powerful
              ? `开始${
                  MAGIC_TYPE_DISPLAY_ENUM.powerful.split("（")[0] || "修图"
                }`
              : `开始${MAGIC_TYPE_DISPLAY_ENUM.video}`}
          </Button>
        )}

        {taskStatus === INFER_TASK_STATUS_ENUM.waiting && (
          <Typography
            variant="body1"
            color="warning"
            fontWeight="bold"
            noWrap
            textAlign="right"
          >
            {`排队中，${totalTasks}个任务`}
          </Typography>
        )}

        {taskStatus === INFER_TASK_STATUS_ENUM.waiting && (
          <Button
            variant="contained"
            onClick={handleMagicCancelTaskClick}
            disabled={taskStatus !== INFER_TASK_STATUS_ENUM.waiting}
            color="warning"
          >
            {INFER_TASK_STATUS_DISPLAY_ENUM.waiting}
          </Button>
        )}

        {props.magicType !== MAGIC_TYPE_ENUM.video && (
          <Button
            variant="outlined"
            onClick={handleReEditClick}
            disabled={!props.magicUrl || generating}
          >
            重新编辑
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleMagicDownloadClick}
          disabled={!props.magicUrl || generating}
        >
          下载
        </Button>

        {props.magicType !== MAGIC_TYPE_ENUM.video && (
          <Button
            variant="outlined"
            onClick={handleContinueEditClick}
            disabled={!props.magicUrl || generating}
          >
            继续编辑
          </Button>
        )}

        <Txt2ImgSizeModal
          open={showTxt2ImgSizePanel}
          onModalClose={(size) => handleTxt2ImgSizeModalClose(size)}
          onOkClick={(size) => {
            handleTxt2ImgSizeModalClose(size);
          }}
        />
      </Box>
    </Grid>
  );
};
